import React, { FC, useMemo } from 'react';

import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { VideoDifficulty } from '../../../../types';
import { useTextStyles } from '../../../../utils/styles';
import { InfoRow } from '../InfoRow';
import useStyles from './styles';

interface InfoBlockProps {
  pathwaysCount?: string | null;
  cpdCredits?: string | null;
  learningHours?: string | null;
  expertsCount?: string | null;
  difficulties: VideoDifficulty[];
  width: Breakpoint;
}

const InfoBlock: FC<InfoBlockProps> = ({
  pathwaysCount,
  cpdCredits,
  learningHours,
  expertsCount,
  difficulties,
  width,
}) => {
  const classes = useStyles();
  const textClasses = useTextStyles();

  const { t } = useTranslation();

  const isMobile = !isWidthUp('sm', width);
  const isTablet = !isWidthUp('md', width);

  const levels = useMemo(() => {
    return difficulties.map((item) => item?.title).join(' & ');
  }, [difficulties]);

  return (
    <Container className={classes.wrapper}>
      <Typography variant="h2" className={cx(textClasses.h2, classes.title)}>
        {t('page.course.atGlance')}
      </Typography>
      <Grid container className={classes.infoWrapper}>
        <Grid item md={4} sm={6} className={classes.infoCol}>
          <InfoRow
            label={t('page.course.contentPathways')}
            value={pathwaysCount}
            isMobile={isMobile}
          />
          <InfoRow label={t('page.course.cpdCredits')} value={cpdCredits} isMobile={isMobile} />
          <InfoRow
            label={t('page.course.learningHours')}
            value={learningHours}
            isMobile={isMobile}
          />
          {isTablet && (
            <>
              <InfoRow
                label={t('page.course.expertPresenters')}
                value={expertsCount}
                isMobile={isMobile}
              />
              <InfoRow
                label={t('page.course.english')}
                value={t('page.course.subtitlesEnglish')}
                isMobile={isMobile}
              />
            </>
          )}
        </Grid>
        {!isTablet && (
          <Grid item md={4} sm={6} className={classes.infoCol}>
            <InfoRow
              label={t('page.course.expertPresenters')}
              value={expertsCount}
              isMobile={isMobile}
            />
            <InfoRow
              label={t('page.course.english')}
              value={t('page.course.subtitlesEnglish')}
              isMobile={isMobile}
            />
            <InfoRow
              label={t('page.course.level', { count: difficulties.length })}
              value={levels}
              isMobile={isMobile}
            />
          </Grid>
        )}
        <Grid item md={4} sm={6} className={classes.infoCol}>
          {isTablet && (
            <InfoRow
              label={t('page.course.level', { count: difficulties.length })}
              value={levels}
              isMobile={isMobile}
            />
          )}
          {isMobile ? (
            <>
              <InfoRow
                label={t('page.course.educationConditions')}
                value={t('page.course.100%Online')}
                isMobile={isMobile}
              />
              <InfoRow
                label={t('page.course.certificateType')}
                value={t('page.course.shareableCertificate')}
                isMobile={isMobile}
              />
            </>
          ) : (
            <>
              <InfoRow
                label={t('page.course.100%Online')}
                value={t('page.course.100%OnlineDesc')}
                isMobile={isMobile}
              />
              <InfoRow
                label={t('page.course.shareableCertificate')}
                value={t('page.course.shareableCertificateDesc')}
                isMobile={isMobile}
              />
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default withWidth()(InfoBlock);
